import { IconCheckmark2Small } from 'central-icons/IconCheckmark2Small';
import { IconPlusSmall } from 'central-icons/IconPlusSmall';
import classNames from 'classnames';
import { useEffect, useState } from 'react';

import { useCompanySelection } from './company-selection-context';
import chipStyles from './entity-chip.module.css';
import type { TooltipAddSource } from './shortcut-source';

/**
 * A chip that adds (or removes) a company — the subsidiary parent or a
 * competitor — from the current filter selection. The tag shape is shared with
 * the shortcut panel above the selection tree. Renders nothing outside the
 * selection context, so the affordance is scoped to the company filter and
 * absent on other tooltip surfaces.
 */
export const AddCompanyControl = ({
  rcid,
  name,
  source,
  testId = 'entity-info-add-company',
}: {
  rcid: number;
  name: string;
  /** Which tooltip control this is, for the add's analytics. */
  source: TooltipAddSource;
  /** Override the testid (competitors reuse `entity-info-competitor`). */
  testId?: string;
}) => {
  const selection = useCompanySelection();
  // What the reader just asked for, held until the real selection catches up.
  // Adding resolves the company's RICS ancestry over the network first, and a
  // chip that sat unchanged (or spun a loader) through that read as a dead
  // control — so the chip answers the click and the commit lands behind it.
  const [optimistic, setOptimistic] = useState<boolean | null>(null);

  const selected = selection?.isCompanySelected(rcid) ?? false;
  const pending = selection?.pendingRcid === rcid;

  // Once the commit settles the optimistic answer has done its job, so drop it
  // and show the truth. This is what catches a commit that DIDN'T land: the
  // ancestry lookup swallows a failed fetch and leaves the company unselected
  // (see `toggleCompany`), and without this the chip would go on showing an add
  // it never made for as long as the tooltip stayed open. Safe against clearing
  // mid-flight because `toggleCompany` marks itself pending synchronously,
  // batched with the click that sets this.
  useEffect(() => {
    if (!pending && optimistic !== null) setOptimistic(null);
  }, [pending, optimistic]);

  if (!selection) return null;

  const showsSelected = optimistic ?? selected;
  const ChipGlyph = showsSelected ? IconCheckmark2Small : IconPlusSmall;

  const toggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!pending) {
      setOptimistic(!selected);
      selection.toggleCompany({ rcid, name }, source);
    }
  };

  return (
    <button
      type="button"
      className={classNames(chipStyles.chip, chipStyles.chipButton)}
      // The chip ellipsizes long names, so the full one has to stay reachable.
      title={name}
      aria-pressed={showsSelected}
      aria-label={showsSelected ? `Remove ${name}` : `Add ${name}`}
      data-testid={testId}
      data-rcid={rcid}
      data-option-label={name}
      // Reports the committed state, not the optimistic one, so a test can
      // still tell a landed add from a clicked one.
      data-selected={selected ? 'true' : 'false'}
      onClick={toggle}
      // Keep focus inside the popover so the click doesn't blur-close the
      // hovered tooltip before the add registers (matching the links above it).
      onMouseDown={(e) => e.preventDefault()}
    >
      <ChipGlyph size="1em" className={chipStyles.chipGlyph} />
      <span className={chipStyles.chipLabel}>{name}</span>
    </button>
  );
};
